// When the source data is new (`GET /api/routing/version`): each source's change marker, its view's
// newest `source_loaded_at` and its row count. The server reloads its shared rows when a marker moves
// (`backend/src/routing/watermark.ts`) and tells the pages the marker of the rows it now serves, set
// when a reload lands — never one it has only seen, or a page would read again and get the old rows.
// Both sides judge "new" by the one rule here, so several App Service instances and a restart agree.

/** A source's change marker: the newest load time of its view's rows, and how many rows. */
export interface Marker {
  /** The newest `source_loaded_at` (ISO), or null when the view has none. */
  loadedAt: string | null;
  n: number;
  /** "<loadedAt or ''>|<n>": the marker as one value, for the log. */
  text: string;
}

export const marker = (loadedAt: string | null, n: number): Marker => ({
  loadedAt,
  n,
  text: `${loadedAt ?? ""}|${n}`,
});

/** `GET /api/routing/version`: no Fabric query in the request; the markers of the rows the server serves. */
export interface SourceVersion {
  /** The open pick tickets served: their newest load and their count. */
  a2000: Marker | null;
  /** The Deposco orders served (the B2B view). */
  deposco: Marker | null;
  /**
   * The A2000 shipments served: the A2000 load the watcher had seen when they were read, and their count
   * — so their second read, 45 s after an A2000 change, reaches the pages too.
   */
  shipments: Marker | null;
  /** When the Deposco job last ran (`vw_routing_deposco_load`), changed orders or not; null when unread. */
  deposcoCheckedAt: string | null;
  /** When each watcher last read its marker (ISO): every 30 s while the pages are used. For checking only. */
  markersReadAt?: { a2000: string | null; deposco: string | null };
  /** `ROUTING_IMPORT` on (`importSwitchOn`): Admin → Import, its Reset and the Worklist's imported chip. */
  importOn: boolean;
}

/**
 * Whether `next` is newer than `prev`: a later load time, or the same load time with another row
 * count (A2000 drops a pick ticket once it ships, a Deposco order leaves the B2B view: MAX stays).
 * An earlier load time is never newer (another instance, a restart, a lagging replica), and neither
 * is a missing marker: the first one is recorded, not acted on.
 */
export function isNewer(prev: Marker | null | undefined, next: Marker | null | undefined): boolean {
  if (!prev || !next) return false;
  const a = prev.loadedAt ?? "";
  const b = next.loadedAt ?? "";
  if (b !== a) return b > a;
  return next.n !== prev.n;
}

/** The sources a page reads again for: each one whose served marker is newer than the last seen. */
const SOURCES = ["a2000", "deposco", "shipments"] as const;

/**
 * A new `/version` answer against the last one seen: whether the page reads its data again, and what
 * it has now seen. The first answer is only recorded; a marker that goes back (another instance, a
 * restart) is not seen, so the newest stays.
 */
export function versionStep(last: SourceVersion | null, now: SourceVersion): { changed: boolean; seen: SourceVersion } {
  const seen: SourceVersion = { ...now };
  let changed = false;
  for (const k of SOURCES) {
    const before = last?.[k] ?? null;
    if (isNewer(before, now[k])) changed = true;
    else if (before) seen[k] = before;
  }
  return { changed, seen };
}

/**
 * Deposco's "checked" as shown: its job's last run, never before its last change (the job that brought a
 * change ran then at least). A failed or stale read of the last run shows the last change; none, null.
 * By time, not text: Fabric gives some times with microseconds, some without.
 */
export function deposcoChecked(checkedAt: string | null, lastChange: string | null): string | null {
  if (!checkedAt) return lastChange;
  if (!lastChange) return checkedAt;
  const a = Date.parse(checkedAt);
  const b = Date.parse(lastChange);
  if (Number.isNaN(a)) return lastChange;
  return !Number.isNaN(b) && b > a ? lastChange : checkedAt;
}
